// Small helpers to display session-related fields consistently across the
// booking flow. Keeping this in ONE place is what avoids the "was that
// UTC or my time?" confusion the client called out.

export const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const WEEKDAY_LONG = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
];

// Common IANA timezones offered in the coach availability editor. Kept
// short on purpose — we can always let coaches type their own if they need
// something exotic.
export const TIMEZONE_CHOICES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Toronto",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "Europe/Lisbon",
  "Africa/Cairo",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
];

export const SESSION_DURATION_CHOICES = [30, 45, 60, 90, 120];

export function labelForMode(mode) {
  if (mode === "online") return "Online session";
  if (mode === "in_person") return "In-person session";
  if (mode === "both") return "Online or in-person";
  return mode || "Session";
}

export function shortModeLabel(mode) {
  if (mode === "online") return "Online";
  if (mode === "in_person") return "In person";
  if (mode === "both") return "Both";
  return mode || "";
}

function _localTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

// Format a UTC ISO string in a target IANA timezone.
export function formatInTz(iso, tz, opts = {}) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: tz || "UTC",
      weekday: opts.weekday || "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...opts.extra,
    });
  } catch {
    return new Date(iso).toISOString();
  }
}

// Short time-only formatter for slot pills — e.g. "9:00 AM".
export function formatTimeInTz(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      timeZone: tz || "UTC",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "";
  }
}

// Short day label for the day-pill row — e.g. "Wed Aug 6".
export function formatDayInTz(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      timeZone: tz || "UTC",
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

// Deterministic day-key in the given timezone (YYYY-MM-DD) so we can group
// UTC slots by "day as the coach's timezone sees it".
export function dayKeyInTz(iso, tz) {
  try {
    // en-CA gives YYYY-MM-DD out of the box.
    return new Date(iso).toLocaleDateString("en-CA", { timeZone: tz || "UTC" });
  } catch {
    return (iso || "").slice(0, 10);
  }
}

export function tzOffsetLabel(tz) {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz || "UTC",
      timeZoneName: "shortOffset",
    }).formatToParts(now);
    const off = parts.find((p) => p.type === "timeZoneName");
    return off ? off.value : "";
  } catch {
    return "";
  }
}

export function localTz() { return _localTimezone(); }

export function isSameTz(a, b) {
  return (a || "").toLowerCase() === (b || "").toLowerCase();
}

export function fmtMoney(cents = 0, currency = "usd") {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  });
}
